import React, {useEffect, useMemo} from 'react';
import {RiSettings2Fill} from 'react-icons/ri';

import Login from 'modules/auth/presenter/login';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import NotFound from 'shared/layout/not-found';
import useNavigation from './util/use-navigation';

import useToken from 'modules/auth/atoms/use-token';

import logo from 'shared/assets/images/logo.png';
import darkLogo from 'shared/assets/images/logo-dark.png';

import useInitialPage from 'shared/hooks/use-initial-page';
import DashboardLayout from 'shared/layout/dashboard';
import useProfile from 'modules/users/hooks/use-profile';
import Profile from 'modules/users/pages/profile';
import categoriesPages from 'modules/categories/pages';
import {IoFastFoodSharp} from 'react-icons/io5';
import subCategoriesPages from 'modules/sub-categories/pages';
import citiesPages from 'modules/cities/pages';
import neighborhoodsPages from 'modules/neighborhoods/pages';
import usersPages from 'modules/users/pages';
import companiesPages from 'modules/companies/pages';
import segmentsPages from 'modules/segments/pages';
import productsPages from 'modules/products/pages';
import paymentMethodsPages from 'modules/payment-methods/pages';
import couponsPages from 'modules/coupons/pages';
import notificationsPages from 'modules/notifications/pages';
import addressesPages from 'modules/addresses/pages';
import additionalGroupsPages from 'modules/additional-groups/pages';
import additionalsPages from 'modules/additionals/pages';
import {ordersPages, manageOrdersPages} from 'modules/orders/pages';
import billingsPages from 'modules/billings/pages';
import deliveryMenPages from 'modules/delivery-men/pages';
import permissionGroupsPages from 'modules/permission-groups/presenter';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import AccessControls from 'modules/access-controls/pages/access-controls';
import logsPages from 'modules/logs/pages';
import Template from './template';
import ResetPasswordRequest from 'modules/auth/presenter/reset-password-request';
import Register from 'modules/auth/presenter/register';
import ResetPasswordConfirm from 'modules/auth/presenter/reset-password-confirm';
import CreateCompany from 'modules/access-controls/pages/create-company';
import walletPages from 'modules/wallet/pages';
import Loading from 'shared/migration-components/loading';
import deliveryManBillingsPages from 'modules/delivery-man-billings/pages';
import reportsPages from 'modules/reports/pages';

import newOrderAudio from 'shared/assets/audio/new-order.mp3';
import {io} from 'socket.io-client';
import {socketUrl} from 'shared/hooks/use-api';
import {infoNotification} from 'shared/services/notification';
import {OrderProps, OrderStatus} from 'modules/orders/types';
import {BiSolidReport} from 'react-icons/bi';
import {SiCashapp} from 'react-icons/si';
import {BsFillHandbagFill} from 'react-icons/bs';
import {PiMegaphoneSimpleFill} from 'react-icons/pi';
import invoicesPages from 'modules/invoices/pages';
import couponredemptionsPages from 'modules/coupon-redemptions/pages';
import whatsappDeliveryErrorsPages from 'modules/whatsapp-delivery-errors/pages';

export interface NavigationProps {
  name: string;
  path: string;
  element?: React.ReactNode;
  icon: React.ReactNode;
  routes?: RouteProps[];
  subRoutes?: SubRouteProps[];
  hideDrawer?: boolean;
  hidden?: boolean;
}

export interface RouteProps {
  name: string;
  path: string;
  hideDrawer?: boolean;
  redirectUrl?: string;
  element?: React.ReactNode;
  modals?: ModalRouteProps[];
  subRoutes?: SubRouteProps[];
  permission: string;
  exactPermission?: boolean;
  icon?: React.ReactNode;
  addCompanyUrl?: boolean;
  addCityUrl?: boolean;
  hidden?: boolean;
}

export interface SubRouteProps {
  path: string;
  element?: React.ReactNode;
  modals?: ModalRouteProps[];
  permissions?: string[];
  hideDrawer?: boolean;
  wrapper?: boolean;
  exactPermission?: boolean;
}

export interface ModalRouteProps {
  path: string;
  title?: string;
  element: React.ReactNode;
  permissions?: string[];
  noLayout?: boolean;
  size?: any;
  centered?: boolean;
  withCloseButton?: boolean;
}

export const navigationConfig: NavigationProps[] = [
  {
    name: 'PEDIDOS',
    path: 'orders',
    icon: <BsFillHandbagFill />,
    subRoutes: ordersPages,
  },
  {
    name: 'CARDÁPIO',
    path: 'menu',
    icon: <IoFastFoodSharp />,
    routes: [
      categoriesPages,
      subCategoriesPages,
      productsPages,
      additionalGroupsPages,
      additionalsPages,
    ],
  },
  {
    name: 'RELATÓRIOS',
    path: 'reports',
    icon: <BiSolidReport />,
    routes: [...reportsPages, manageOrdersPages, couponredemptionsPages],
  },
  {
    name: 'FINANCEIRO',
    path: 'financial',
    icon: <SiCashapp />,
    routes: [billingsPages, deliveryManBillingsPages, invoicesPages],
  },
  {
    name: 'MARKETING',
    path: 'marketing',
    icon: <PiMegaphoneSimpleFill style={{transform: 'scaleX(-1)'}} />,
    routes: [walletPages, notificationsPages],
  },
  {
    name: 'CONFIGURAÇÕES',
    path: 'settings',
    icon: <RiSettings2Fill />,
    routes: [
      ...citiesPages,
      neighborhoodsPages,
      usersPages,
      ...companiesPages,
      segmentsPages,
      paymentMethodsPages,
      addressesPages,
      deliveryMenPages,
      permissionGroupsPages,
      logsPages,
      whatsappDeliveryErrorsPages,
    ],
  },
];

const NavigationConfig: React.FC = () => {
  const initialPage = useInitialPage();

  const {token} = useToken();
  const {cityId, companyId, isFranchiseAccessControl} = useAccessControls();
  const navigation = useNavigation(navigationConfig);
  const {data, isLoading} = useProfile();
  const theme = localStorage.getItem('theme');

  const socket = useMemo(
    () => io(socketUrl, {transports: ['websocket']}),
    [socketUrl],
  );

  useEffect(() => {
    return () => {
      socket.disconnect();
    };
  }, [socket]);

  const socketKey = cityId ?? companyId;

  useEffect(() => {
    if (!socketKey) return;

    const handler = (data: OrderProps) => {
      if (data.currentStatusName === OrderStatus.opened) {
        const audio = new Audio(newOrderAudio);
        audio.play();
        infoNotification({
          title: 'Novo pedido recebido',
          message: 'Novo pedido recebido, por favor verifique',
          onClick: () =>
            window.open(`https://painel.uaudelivery.com.br/orders/${data.id}`),
        });
      }
    };

    socket.on(socketKey, handler);
    return () => {
      socket.off(socketKey, handler);
    };
  }, [socket, socketKey]);

  useEffect(() => {
    if (!socketKey) return;

    const eventName = `orders:opened:${socketKey}`;
    const handler = (data: OrderProps) => {
      const audio = new Audio(newOrderAudio);
      audio.play();
      infoNotification({
        title: 'Novo pedido recebido',
        message: 'Novo pedido recebido, por favor verifique',
        onClick: () =>
          window.open(`https://painel.uaudelivery.com.br/orders/${data.id}`),
      });
    };

    socket.on(eventName, handler);
    return () => {
      socket.off(eventName, handler);
    };
  }, [socket, socketKey]);

  if (!data && isLoading)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          gap: 20,
        }}>
        <img
          src={theme === 'dark' ? darkLogo : logo}
          style={{
            width: 180,
            height: 120,
            alignSelf: 'center',
            marginLeft: -20,
          }}
        />
        <Loading flex={false} size={28} />
      </div>
    );

  return (
    <BrowserRouter {...{basename: import.meta.env.VITE_BASE_PATH ?? ''}}>
      <Routes>
        <Route element={<Template />} path="/template" />
        <Route
          element={token ? <Navigate to="/" /> : <Login />}
          path="/login"
        />
        <Route
          element={token ? <Navigate to="/" /> : <Register />}
          path="/register"
        />
        <Route
          element={token ? <Navigate to="/" /> : <ResetPasswordRequest />}
          path="/reset-password/request"
        />
        <Route
          element={token ? <Navigate to="/" /> : <ResetPasswordConfirm />}
          path="/reset-password/confirm"
        />
        {token && (cityId || companyId || isFranchiseAccessControl) && (
          <>
            {navigation}
            <Route
              path="/profile"
              element={
                <DashboardLayout>
                  <Profile />
                </DashboardLayout>
              }
            />
            <Route path="/" element={<Navigate to={initialPage} />} />
          </>
        )}
        {(!cityId || !companyId || !isFranchiseAccessControl) && (
          <Route path="/access-controls" element={<AccessControls />} />
        )}
        {token && !cityId && !companyId && !isFranchiseAccessControl && (
          <Route element={<CreateCompany />} path="/create-company" />
        )}
        <Route
          path="*"
          element={
            token ? (
              !cityId || !companyId || !isFranchiseAccessControl ? (
                <Navigate to="/access-controls" />
              ) : (
                <NotFound />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default NavigationConfig;
